/**
 * Owner: Masa Bayu
 * File: AuthorDiscoverySections.tsx
 * Date:Time: 2026-10-23 00:11
 * Description: §411 — blok UI Author Discovery: baris pil, kad Featured
 *   (desktop), barisan avatar Popular (mobile), senarai bernombor Terbit
 *   Terkini, grid Kategori Popular, dan karusel "Latest Books by Author".
 *   Semua tindakan wired: Follow → stor, kad → /author/:id, buku →
 *   /publication/:id, Read → /read/:id, kategori → /all-books?category=.
 *   §466 S1 — kiraan penilaian rekaan diganti harga sebenar pada kad buku.
 */

import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { qxCategoryName } from '@qx/qxread-core';
import type { QxMainCategoryId } from '@qx/qxread-core';
import { LandingIcon } from '../../home/marketplace/landing/LandingIcons';
import { CATEGORY_ICON } from '../../home/marketplace/landing/landing-shared';
import { formatMarketplacePriceOrUnknown } from '../../home/marketplace/marketplace-locale';
import type { MarketplaceBook } from '../../home/marketplace/marketplace-projection';
import type { PublicAuthor } from '../public/public-author-data';
import { AuthorAvatar } from './AuthorAvatar';
import { AuthorFollowButton } from './AuthorFollowButton';
import { AUTHOR_PILL_ORDER, type AuthorPill } from './author-discovery-data';
import type { AuthorScreensLabels } from './author-discovery-labels';
import type { PopularCategoryTile } from './author-discovery-data';

type Language = 'en' | 'ms';

interface SectionProps {
  readonly t: AuthorScreensLabels;
  readonly language: Language;
  readonly onNavigate: (path: string) => void;
}

export function genreLine(author: PublicAuthor, language: Language): string {
  return author.genres
    .slice(0, 2)
    .map((g) => qxCategoryName(g, language))
    .join(' · ');
}

/* ---------- Pil penuras (All / Fiction / Non-Fiction / Popular) ---------- */

export function AuthorPillRow({
  t,
  pill,
  onPill,
  className,
}: {
  t: AuthorScreensLabels;
  pill: AuthorPill;
  onPill: (pill: AuthorPill) => void;
  className?: string;
}): ReactElement {
  return (
    <div className={className ?? 'qxdsc__pills'} role="group" aria-label={t.discovery.heroAria}>
      {AUTHOR_PILL_ORDER.map((id) => (
        <button
          key={id}
          type="button"
          className={id === pill ? 'qxdsc__pill is-active' : 'qxdsc__pill'}
          aria-pressed={id === pill}
          onClick={() => {
            onPill(id);
          }}
        >
          {t.discovery.pills[id]}
        </button>
      ))}
    </div>
  );
}

/* ---------- Kepala seksyen + View All ---------- */

function SectionHead({
  title,
  viewAllLabel,
  onSeeAll,
}: {
  title: string;
  viewAllLabel: string;
  onSeeAll: (() => void) | null;
}): ReactElement {
  return (
    <div className="qxdsc__head">
      <h2 className="qxdsc__h2">{title}</h2>
      {onSeeAll !== null ? (
        <button type="button" className="qxdsc__viewall" onClick={onSeeAll}>
          {viewAllLabel}
          <LandingIcon name="arrowRight" size={14} />
        </button>
      ) : null}
    </div>
  );
}

/* ---------- Featured (desktop): kad penuh ---------- */

export function FeaturedAuthorCards({
  t,
  language,
  authors,
  expanded,
  following,
  onToggleFollow,
  onSeeAll,
  onNavigate,
}: SectionProps & {
  authors: readonly PublicAuthor[];
  expanded: boolean;
  following: ReadonlySet<string>;
  onToggleFollow: (id: string) => void;
  onSeeAll: () => void;
}): ReactElement {
  const shown = expanded ? authors : authors.slice(0, 5);
  return (
    <section className="qxdsc__section qxdsc__featured" aria-label={t.discovery.featuredHeading}>
      <SectionHead
        title={t.discovery.featuredHeading}
        viewAllLabel={t.discovery.viewAll}
        onSeeAll={authors.length > 5 ? onSeeAll : null}
      />
      {shown.length === 0 ? (
        <p className="qxdsc__empty">{t.discovery.emptyAuthors}</p>
      ) : (
        <ul className="qxdsc__fgrid" role="list">
          {shown.map((author) => (
            <li key={author.id} className="qxdsc__fcard">
              <button
                type="button"
                className="qxdsc__fcard-hit"
                onClick={() => {
                  onNavigate(`/author/${author.id}`);
                }}
              >
                <span className="qxdsc__favatar">
                  <AuthorAvatar initials={author.initials} seed={author.id} />
                </span>
                <span className="qxdsc__fname">{author.name}</span>
                <span className="qxdsc__fgenre">{genreLine(author, language)}</span>
                <span className="qxdsc__fmeta">
                  <LandingIcon name="book" size={14} />
                  {`${author.books.length} ${t.discovery.booksUnit}`}
                </span>
              </button>
              <AuthorFollowButton
                following={following.has(author.id)}
                onToggle={() => {
                  onToggleFollow(author.id);
                }}
                followLabel={t.discovery.follow}
                followingLabel={t.discovery.following}
                ariaName={author.name}
              />
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------- Popular (mobile): barisan avatar ---------- */

export function PopularAuthorRow({
  t,
  authors,
  onNavigate,
}: SectionProps & { authors: readonly PublicAuthor[] }): ReactElement {
  const shown = authors.slice(0, 5);
  return (
    <section className="qxdsc__section qxdsc__popular-row" aria-label={t.discovery.popularHeading}>
      <SectionHead
        title={t.discovery.popularHeading}
        viewAllLabel={t.discovery.viewAll}
        onSeeAll={null}
      />
      {shown.length === 0 ? (
        <p className="qxdsc__empty">{t.discovery.emptyAuthors}</p>
      ) : (
        <ul className="qxdsc__arow" role="list">
          {shown.map((author) => (
            <li key={author.id} className="qxdsc__arow-item">
              <button
                type="button"
                className="qxdsc__arow-hit"
                onClick={() => {
                  onNavigate(`/author/${author.id}`);
                }}
              >
                <span className="qxdsc__arow-avatar">
                  <AuthorAvatar initials={author.initials} seed={author.id} />
                </span>
                <span className="qxdsc__arow-name">{author.name}</span>
                <span className="qxdsc__arow-count">
                  {`${author.books.length} ${t.discovery.booksLine}`}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ---------- Trending: senarai bernombor ---------- */

export function TrendingAuthorList({
  t,
  authors,
  expanded,
  following,
  onToggleFollow,
  onSeeAll,
  onNavigate,
}: SectionProps & {
  authors: readonly PublicAuthor[];
  expanded: boolean;
  following: ReadonlySet<string>;
  onToggleFollow: (id: string) => void;
  onSeeAll: () => void;
}): ReactElement {
  const shown = expanded ? authors : authors.slice(0, 5);
  return (
    <section className="qxdsc__card" aria-label={t.discovery.recentAria}>
      <div className="qxdsc__card-head">
        <span className="qxdsc__card-icon" aria-hidden="true">
          <LandingIcon name="flame" size={16} />
        </span>
        <h2 className="qxdsc__h2">{t.discovery.recentHeading}</h2>
        <button type="button" className="qxdsc__viewall qxdsc__viewall--right" onClick={onSeeAll}>
          {t.discovery.viewAll}
          <LandingIcon name="arrowRight" size={14} />
        </button>
      </div>
      {shown.length === 0 ? (
        <p className="qxdsc__empty">{t.discovery.emptyAuthors}</p>
      ) : (
        <ol className="qxdsc__tlist" role="list">
          {shown.map((author, index) => (
            <li key={author.id} className="qxdsc__trow">
              <span className="qxdsc__trank">{index + 1}</span>
              <button
                type="button"
                className="qxdsc__thit"
                onClick={() => {
                  onNavigate(`/author/${author.id}`);
                }}
              >
                <span className="qxdsc__tavatar">
                  <AuthorAvatar initials={author.initials} seed={author.id} />
                </span>
                <span className="qxdsc__ttext">
                  <span className="qxdsc__tname">{author.name}</span>
                  <span className="qxdsc__tcount">
                    {`${author.books.length} ${t.discovery.booksLine}`}
                  </span>
                </span>
              </button>
              <AuthorFollowButton
                following={following.has(author.id)}
                onToggle={() => {
                  onToggleFollow(author.id);
                }}
                followLabel={t.discovery.follow}
                followingLabel={t.discovery.following}
                ariaName={author.name}
              />
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/* ---------- Kategori Popular ---------- */

export function PopularCategoryGrid({
  t,
  language,
  tiles,
  onNavigate,
}: SectionProps & { tiles: readonly PopularCategoryTile[] }): ReactElement {
  return (
    <section className="qxdsc__card" aria-label={t.discovery.categoriesAria}>
      <div className="qxdsc__card-head">
        <span className="qxdsc__card-icon" aria-hidden="true">
          <LandingIcon name="bookmark" size={16} />
        </span>
        <h2 className="qxdsc__h2">{t.discovery.categoriesHeading}</h2>
        <button
          type="button"
          className="qxdsc__viewall qxdsc__viewall--right"
          aria-label={t.discovery.categoriesHeading}
          onClick={() => {
            onNavigate('/categories');
          }}
        >
          <LandingIcon name="chevronRight" size={16} />
        </button>
      </div>
      <ul className="qxdsc__catgrid" role="list">
        {tiles.map((tile) => (
          <li key={tile.categoryId}>
            <button
              type="button"
              className="qxdsc__catcard"
              onClick={() => {
                onNavigate(`/all-books?category=${tile.categoryId}`);
              }}
            >
              <span className="qxdsc__cat-icon" aria-hidden="true">
                <LandingIcon name={CATEGORY_ICON[tile.categoryId]} size={18} />
              </span>
              <span className="qxdsc__cat-text">
                <span className="qxdsc__cat-name">{qxCategoryName(tile.categoryId, language)}</span>
                <span className="qxdsc__cat-count">
                  {`${tile.authorCount} ${t.discovery.authorsUnit}`}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/* ---------- Karusel buku paling popular ---------- */

export function BooksByAuthorRail({
  t,
  language,
  books,
  tiles,
  onNavigate,
}: SectionProps & {
  books: readonly MarketplaceBook[];
  tiles: readonly PopularCategoryTile[];
}): ReactElement {
  const [cat, setCat] = useState<QxMainCategoryId | 'all'>('all');
  const scroller = useRef<HTMLOListElement | null>(null);
  const shown = cat === 'all' ? books : books.filter((b) => b.categoryId === cat);
  const scrollBy = (delta: number): void => {
    scroller.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };
  return (
    <section className="qxdsc__section qxdsc__books" aria-label={t.discovery.latestBooksAria}>
      <SectionHead
        title={t.discovery.latestBooksHeading}
        viewAllLabel={t.discovery.viewAll}
        onSeeAll={null}
      />
      <div className="qxdsc__book-pills" role="group" aria-label={t.discovery.latestBooksHeading}>
        <button
          type="button"
          className={cat === 'all' ? 'qxdsc__pill is-active' : 'qxdsc__pill'}
          aria-pressed={cat === 'all'}
          onClick={() => {
            setCat('all');
          }}
        >
          {t.discovery.pills.all}
        </button>
        {tiles.slice(0, 5).map((tile) => (
          <button
            key={tile.categoryId}
            type="button"
            className={cat === tile.categoryId ? 'qxdsc__pill is-active' : 'qxdsc__pill'}
            aria-pressed={cat === tile.categoryId}
            onClick={() => {
              setCat(tile.categoryId);
            }}
          >
            {qxCategoryName(tile.categoryId, language)}
          </button>
        ))}
        <button
          type="button"
          className="qxdsc__pill qxdsc__pill--more"
          onClick={() => {
            onNavigate('/categories');
          }}
        >
          {t.discovery.more}
          <LandingIcon name="chevronLeft" size={12} />
        </button>
      </div>
      <div className="qxdsc__books-stage">
        <button
          type="button"
          className="qxdsc__book-arrow"
          aria-label="Previous"
          onClick={() => {
            scrollBy(-320);
          }}
        >
          <LandingIcon name="chevronLeft" size={16} />
        </button>
        {shown.length === 0 ? (
          <p className="qxdsc__empty">{t.discovery.emptyBooks}</p>
        ) : (
          <ol ref={scroller} className="qxdsc__book-rail" role="list">
            {shown.map((book) => (
              <li key={book.publicationId} className="qxdsc__book-card">
                <button
                  type="button"
                  className="qxdsc__book-hit"
                  onClick={() => {
                    onNavigate(`/publication/${book.publicationId}`);
                  }}
                >
                  <span className="qxdsc__book-cover" data-tone={book.coverTone} aria-hidden="true">
                    <span className="qxdsc__book-cover-title">{book.title}</span>
                  </span>
                  <span className="qxdsc__book-title">{book.title}</span>
                  {book.author === null ? null : (
                    <span className="qxdsc__book-author">{book.author}</span>
                  )}
                  <span className="qxdsc__book-pop">
                    {formatMarketplacePriceOrUnknown(
                      book.priceMinorUnits,
                      book.currency,
                      t.discovery.priceUnknown,
                    )}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        )}
        <button
          type="button"
          className="qxdsc__book-arrow"
          aria-label="Next"
          onClick={() => {
            scrollBy(320);
          }}
        >
          <LandingIcon name="chevronRight" size={16} />
        </button>
      </div>
    </section>
  );
}
